"use client";

import React from "react";
import { MdArrowLeft, MdArrowRight } from "react-icons/md";
import { ExternalLinkIcon, PlusIcon } from "lucide-react";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  DEVIS_STATUS,
  type DevisClient,
} from "modules/devis/types/devis.interface";
import SearchAndFilterBar from "./filter/searchAndFilterBar";

export type RowObjDevis = {
  _id: string;
  documentNumber: { number: string; prefix: string; suffix: string };
  totalHT: number;
  totalTTC: number;
  dueDate: Date;
  createdAt: Date;
  updatedAt: Date;
  status: DEVIS_STATUS;
  archived: boolean;
  lineItems: any[];
  client: DevisClient;
  ebpId?: string;
  workspaceId: string;
  interveners?: string[];
};

const columnHelper = createColumnHelper<RowObjDevis>();

const formatCurrency = (v: number) =>
  Intl.NumberFormat("fr-FR").format(v) + " \u20AC";

const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
  [DEVIS_STATUS.DRAFT]: { label: "Brouillon", bg: "bg-gray-100", text: "text-gray-600" },
  [DEVIS_STATUS.SENT]: { label: "Envoyé", bg: "bg-indigo-50", text: "text-indigo-700" },
  [DEVIS_STATUS.ACCEPTED]: { label: "Accepté", bg: "bg-emerald-50", text: "text-emerald-700" },
  [DEVIS_STATUS.REJECTED]: { label: "Refusé", bg: "bg-red-50", text: "text-red-700" },
  [DEVIS_STATUS.VALIDATED]: { label: "Validé", bg: "bg-blue-50", text: "text-blue-700" },
};

export default function DevisTable(props: {
  tableData: RowObjDevis[];
  searchName: string;
  setSearchName: any;
  currentPage: number;
  totalPage: number;
  hasNextPage: boolean;
  handleFetchNextPage: () => void;
  handleFetchPrevPage: () => void;
  isLoading: boolean;
  setFilters: (value: any) => void;
  filters: any;
}) {
  const {
    tableData,
    handleFetchNextPage,
    hasNextPage,
    handleFetchPrevPage,
    totalPage,
    currentPage,
    isLoading,
    setFilters,
  } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("documentNumber.number", {
        id: "ID",
        header: () => <span>N°</span>,
        cell: (info) => (
          <span className="font-mono text-xs text-gray-500">
            {info.getValue() || "-"}
          </span>
        ),
      }),
      columnHelper.accessor("client", {
        id: "Client",
        header: () => <span>Client</span>,
        cell: (info) => (
          <span className="font-medium text-gray-900">
            {info.getValue()?.name || "-"}
          </span>
        ),
      }),
      columnHelper.accessor("createdAt", {
        id: "Date",
        header: () => <span>Date</span>,
        cell: (info) => (
          <span className="text-gray-600">
            {new Date(info.getValue()).toLocaleDateString("fr-FR")}
          </span>
        ),
      }),
      columnHelper.accessor("totalHT", {
        id: "HT",
        header: () => <span>HT</span>,
        cell: (info) => (
          <span className="text-gray-600">{formatCurrency(info.getValue())}</span>
        ),
      }),
      columnHelper.accessor("totalTTC", {
        id: "TTC",
        header: () => <span>TTC</span>,
        cell: (info) => (
          <span className="font-medium text-gray-900">
            {formatCurrency(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor("status", {
        id: "Statut",
        header: () => <span>Statut</span>,
        cell: (info) => {
          const s = statusConfig[info.getValue()] || statusConfig[DEVIS_STATUS.DRAFT];
          return (
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}
            >
              {s.label}
            </span>
          );
        },
      }),
      columnHelper.accessor("_id", {
        id: "actions",
        header: () => <span></span>,
        cell: (info) => (
          <a
            href={`/admin/devis/${info.getValue()}`}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <ExternalLinkIcon className="h-4 w-4" />
          </a>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h3 className="text-base font-semibold text-gray-900">Devis</h3>
        <a
          href="/admin/devis/create"
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Nouveau
        </a>
      </div>

      <div className="px-5 pt-3">
        <SearchAndFilterBar
          searchValue={props.searchName}
          onSearchChange={props.setSearchName}
          onFilterChange={setFilters}
          type="devis"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
        </div>
      ) : tableData.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-400">Aucun devis trouvé</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-50">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="transition-colors hover:bg-gray-50/50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="whitespace-nowrap px-5 py-3 text-sm">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
        <span className="text-xs text-gray-400">
          Page {currentPage} sur {totalPage}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={handleFetchPrevPage}
            disabled={currentPage <= 1}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"
          >
            <MdArrowLeft className="h-5 w-5" />
          </button>
          <button
            onClick={handleFetchNextPage}
            disabled={!hasNextPage}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"
          >
            <MdArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
